import React, { useState, useEffect, useContext } from "react";

import Aos from "aos";
import "aos/dist/aos.css";

import { useParams } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { ArtContext } from "../Contexts/ArtContext";
import { ArtistContext } from "../Contexts/ArtistContext";
import Loading from "../pages/Loading";
import ReactGA from "react-ga";
import Alert from "../components/Alert";

ReactGA.pageview(window.location.pathname + window.location.search);

const ProductDetails = () => {
  const { id } = useParams();
  const { getArt, fetchArtsByCategoryOrCollection } = useContext(ArtContext);
  const { getArtist } = useContext(ArtistContext);
  const [art, setArt] = useState(null);
  const [artistName, setArtistName] = useState("");
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [price, setPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ status: "", message: "" });

  const itemsPerPage = 9;
  const [currentPage, setCurrentPage] = useState(1);
  const [relatedArts, setRelatedArts] = useState([]);

  const [cart, setCart] = useState(() => {
    // Load the cart from localStorage on initial render
    const savedCart = localStorage.getItem("cart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    Aos.init({ duration: 100 });
  }, []);

  useEffect(() => {
    const fetchArtDetails = async () => {
      try {
        const fetchedArt = await getArt(id);
        if (fetchedArt) {
          setArt(fetchedArt);
          const initialEdition = fetchedArt.editions[0];
          setSelectedEdition(initialEdition);
          setPrice(initialEdition.price);

          const artist = await getArtist(fetchedArt.artists);
          setArtistName(artist.name);

          // Fetch related arts based on category or collection
          let relatedArtworks = [];
          if (fetchedArt.categories && fetchedArt.categories.length > 0) {
            relatedArtworks = await fetchArtsByCategoryOrCollection(
              fetchedArt.categories[0]
            );
          } else if (
            fetchedArt.collections &&
            fetchedArt.collections.length > 0
          ) {
            relatedArtworks = await fetchArtsByCategoryOrCollection(
              fetchedArt.collections[0]
            );
          }
          setRelatedArts(relatedArtworks);
        } else {
          console.error("Art not found for id:", id);
        }
      } catch (error) {
        console.error("Error fetching art details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArtDetails();
  }, [id]);

  const handleEditionChange = (event) => {
    const selectedEditionType = event.target.value;
    const selectedEdition = art.editions.find(
      (edition) => edition.editionType === selectedEditionType
    );
    setSelectedEdition(selectedEdition);
    setPrice(selectedEdition.price);
  };

  const handleClickNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const addToCart = (product) => {
    const productToAdd = {
      id: product.id,
      name: product.artName,
      price: selectedEdition.price,
      artImageCover: product.artImageCover,
      edition: selectedEdition.editionType,
      quantity: 1,
    };

    // Check if the product is already in the cart
    const existingProduct = cart.find(
      (item) =>
        item.id === product.id && item.edition === selectedEdition.editionType
    );

    let updatedCart;
  
    // Check if the same artwork with "Original Art Work" edition is already in the cart
    if (selectedEdition.editionType.includes("Original Art Work")) {
      // Prevent adding more than one original artwork
      const originalArt = cart.find((item) =>
        item.edition.includes("Original Art Work")
      );
  
      if (originalArt) {
        // Show alert that the same original artwork can only be added once
        setAlert({
          status: "error",
          message: "You can only add one Original Artwork of this piece to the cart.",
        });
        return;
      }
    }

    if (existingProduct) {
      // Update quantity if the product and edition already exist
      updatedCart = cart.map((item) =>
        item.id === product.id && item.edition === selectedEdition.editionType
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      // Add new product to the cart
      updatedCart = [...cart, productToAdd];
    }

    // Save updated cart to localStorage
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));

    // Show success alert when artwork is added
    setAlert({ status: "success", message: "Artwork added to cart" });
  
    // Show success alert when artwork is added
    setAlert({ status: "success", message: "Artwork added to cart" });
  };

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const handleClickPrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (!art) {
    return <Loading />;
  }

  const totalPages = Math.ceil(relatedArts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = relatedArts.slice(startIndex, startIndex + itemsPerPage);

  const renderPageNumbers = () => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => (
        <span
          key={index}
          className={`mx-1 ${
            currentPage === index + 1 ? "text-black" : "text-gray-400"
          } font-semibold text-xl md:text-2xl`}
        >
          {index + 1}
        </span>
      ));
    }

    if (currentPage <= 2) {
      return (
        <>
          <span
            className={`mx-1 ${
              currentPage === 1 ? "text-black" : "text-gray-400"
            } font-semibold text-xl md:text-2xl`}
          >
            1
          </span>
          <span
            className={`mx-1 ${
              currentPage === 2 ? "text-black" : "text-gray-400"
            } font-semibold text-xl md:text-2xl`}
          >
            2
          </span>
          <span className="mx-1 text-gray-400 font-semibold text-xl md:text-2xl">
            ...
          </span>
          <span
            className={`mx-1 ${
              currentPage === totalPages ? "text-black" : "text-gray-400"
            } font-semibold text-xl md:text-2xl`}
          >
            {totalPages}
          </span>
        </>
      );
    }

    if (currentPage >= totalPages - 1) {
      return (
        <>
          <span
            className={`mx-1 ${
              currentPage === 1 ? "text-black" : "text-gray-400"
            } font-semibold text-xl md:text-2xl`}
          >
            1
          </span>
          <span className="mx-1 text-gray-400 font-semibold text-xl md:text-2xl">
            ...
          </span>
          <span
            className={`mx-1 ${
              currentPage === totalPages - 1 ? "text-black" : "text-gray-400"
            } font-semibold text-xl md:text-2xl`}
          >
            {totalPages - 1}
          </span>
          <span
            className={`mx-1 ${
              currentPage === totalPages ? "text-black" : "text-gray-400"
            } font-semibold text-xl md:text-2xl`}
          >
            {totalPages}
          </span>
        </>
      );
    }

    return (
      <>
        <span
          className={`mx-1 ${
            currentPage === 1 ? "text-black" : "text-gray-400"
          } font-semibold text-xl md:text-2xl`}
        >
          1
        </span>
        <span className="mx-1 text-gray-400 font-semibold text-xl md:text-2xl">
          ...
        </span>
        <span
          className={`mx-1 ${
            currentPage ? "text-black" : "text-gray-400"
          } font-semibold text-xl md:text-2xl`}
        >
          {currentPage}
        </span>
        <span className="mx-1 text-gray-400 font-semibold text-xl md:text-2xl">
          ...
        </span>
        <span
          className={`mx-1 ${
            currentPage === totalPages ? "text-black" : "text-gray-400"
          } font-semibold text-xl md:text-2xl`}
        >
          {totalPages}
        </span>
      </>
    );
  };

  return (
    <div>
      <NavBar />
      {alert.message && (
        <Alert
          status={alert.status}
          message={alert.message}
          onClose={() => setAlert({ status: "", message: "" })}
        />
      )}
      <div className="flex flex-col items-center justify-center min-h-screen p-8 py-12">
        <div className="flex flex-col lg:flex-row rounded-lg shadow-lg overflow-hidden w-full max-w-screen-xl py-10 px-2">
          <div className="lg:w-2/4 px-2">
            <img
              data-aos="fade-right"
              data-aos-duration="600"
              src={`${art.artImageCover}`}
              alt={art.artName}
              className="w-full object-cover"
            />
            <div className="flex justify-center mt-4 space-x-2">
              {art.artImages.map((image, index) => (
                <img
                  key={index}
                  src={`${image}`}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-1/3"
                  data-aos="fade-left"
                  data-aos-duration="500"
                  data-aos-delay={index * 500}
                />
              ))}
            </div>
          </div>
          <div
            className="lg:w-2/4 px-2 py-10 lg:py-0"
            data-aos="fade-left"
            data-aos-duration="600"
          >
            <h2 className="text-2xl font-bold mb-4">{art.artName}</h2>
            <p className="text-2xl text-gray-900 mb-4 font-bold">${price}</p>
            <h2 className="text-2xl font-bold mb-4">{art.artName}</h2>
            <p className="text-2xl text-gray-900 mb-4 font-bold">${price}</p>
            <p className="text-2xl text-gray-600 mb-4">
              Size: {art.artSize}
              <br />
              Material: {art.artMaterial}
              <br />
              by{" "}
              <span className="text-primary font-semibold">
                <a href={`/artistDetails/${art.artists}`}>{artistName}</a>
              </span>
            </p>
            <div className="relative inline-block w-full mb-4">
              <select
                onChange={handleEditionChange}
                className="w-full bg-gray-100 text-gray-700 p-4 rounded border border-gray-300 flex items-center justify-between"
              >
                {art.editions.map((edition, index) => (
                  <option key={index} value={edition.editionType}>
                    {edition.editionType}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="w-full bg-black text-white p-6 rounded-lg font-bold text-2xl"
              onClick={() => addToCart(art)}
            >
              Add to Cart
            </button>
            <p className="text-xl text-gray-700 mt-6">{art.artDescription}</p>
          </div>
        </div>
      </div>

      <div className="py-1 mb-16 px-4 md:px-24 mt-8">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-4xl md:text-6xl ml-4">Other Works</h2>
          <a
            href="/shop"
            className="text-black underline text-xl md:text-2xl font-semibold underline-offset-8 decoration-1"
          >
            View all
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {currentItems.map((item) => (
            <div key={item._id} className="text-black p-4 rounded-lg">
              <a href={`/product/${item._id}`}>
                <img
                  src={`${item.artImageCover}`}
                  alt={item.artName}
                  className="w-full h-auto mb-4 rounded-lg"
                />
              </a>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">
                {item.artName}
              </h3>
              <p className="text-xl md:text-2xl">{item.artistName}</p>
              <p className="text-xl md:text-2xl font-light">
                ${item.editions[0].price}
              </p>
            </div>
          ))}
        </div>
        <div className="flex justify-center items-center mt-8">
          <button
            onClick={handleClickPrev}
            className={`mr-4 ${
              currentPage === 1 ? "text-gray-400" : "text-black"
            } text-xl md:text-2xl font-semibold`}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <div className="flex space-x-2">{renderPageNumbers()}</div>
          <button
            onClick={handleClickNext}
            className={`ml-4 ${
              currentPage === totalPages ? "text-gray-400" : "text-black"
            } text-xl md:text-2xl font-semibold`}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProductDetails;
